"use client";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  AccountLimit,
  AccountType,
  AddressInputs,
  Country,
  PersonalInfoInputs,
  Step,
} from "./models";
import {
  FundSource,
  IdentificationType,
  Nationality,
  Occupation,
} from "cy-api/simple-payments/ref-data/getOccupationsServer";
import { useEffect } from "react";
import {
  getReferralCode,
  setReferralCode as setReferralCodeInStorage,
} from "cy-remittance/registration/referral/referralStorage";

export function StateLoader({
  sourceOfFunds,
  nationalities,
  occupations,
  identificationTypes,
}: {
  occupations: Occupation[];
  sourceOfFunds: FundSource[];
  nationalities: Nationality[];
  identificationTypes: IdentificationType[];
}) {
  const state = useRegisterStore.getState();
  const setRefData = state.setRefData;
  useEffect(() => {
    setRefData({
      nationalities,
      occupations,
      sourceOfFunds,
      identificationTypes,
    });
  }, [
    nationalities,
    occupations,
    sourceOfFunds,
    setRefData,
    identificationTypes,
  ]);

  return null;
}

export interface RegisterState {
  accountType: AccountType | undefined;
  accountLimit: AccountLimit | undefined;
  password: string | undefined;
  email: string | undefined;
  registrationId: string | undefined;
  country: Country | undefined;
  mobile: { isd: string; number: string };
  otpConfirmed: boolean | undefined;
  personalInfo: PersonalInfoInputs | undefined;
  addressInfo: AddressInputs | undefined;
  sourceOfFunds: FundSource[] | undefined;
  occupations: Occupation[] | undefined;
  nationalities: Nationality[] | undefined;
  identificationTypes: IdentificationType[] | undefined;
  passwordSet: boolean;
  marketingConsents: Record<string, boolean> | undefined;
  referralCode?: string;
  done: boolean;
}

export interface RegisterActions {
  setAccountType: (args: AccountType) => void;
  setAccountLimit: (args: AccountLimit | undefined) => void;
  setPassword: (args: string) => void;
  updateEmailStep: (args: { email: string; id: string }) => void;
  setCountry: (args: { country: Country }) => void;
  setOtpConfirmed: () => void;
  reset: () => void;
  setMobile: (args: { isd: string; number: string }) => void;
  setPersonalInfo: (args: { personalInfo: PersonalInfoInputs }) => void;
  setAddressInfo: (args: { addressInfo: AddressInputs | undefined }) => void;
  setPasswordSet: () => void;
  setMarketingConsents: (args: Record<string, boolean>) => void;
  setReferralCode: (code: string | undefined) => void;
  setRefData: (args: {
    sourceOfFunds: FundSource[];
    occupations: Occupation[];
    nationalities: Nationality[];
    identificationTypes: IdentificationType[];
  }) => void;
}

export function getCurrentStep(state: RegisterState): Step | null {
  if (!state.mobile.number) {
    return "start";
  }
  if (!state.accountType) {
    return "accountType";
  }
  if (!state.accountType.label && !state.accountLimit) {
    return "accountLimit";
  }
  if (!state.personalInfo) {
    return "personal";
  }
  if (!state.marketingConsents) {
    return "marketing";
  }
  if (!state.passwordSet) {
    return "password";
  }
  if (!state.otpConfirmed) {
    return "otp";
  }
  if (state.done) {
    return "done";
  }

  return null;
}

/** Fresh visit or completed signup. Not Back from a later wizard step. */
export function shouldResetRegistrationOnStart(
  currentStep: Step | null,
): boolean {
  return (
    currentStep === "start" || currentStep === "done" || currentStep == null
  );
}
export interface RegisterStateWithActions
  extends RegisterState,
    RegisterActions {}

function initialState(): RegisterState {
  // Load referral code from sessionStorage on initialization
  const storedReferralCode = getReferralCode();

  return {
    accountType: undefined,
    accountLimit: undefined,
    password: undefined,
    registrationId: undefined,
    mobile: { number: "", isd: "" },
    country: undefined,
    email: undefined,
    otpConfirmed: undefined,
    personalInfo: undefined,
    occupations: undefined,
    sourceOfFunds: undefined,
    addressInfo: undefined,
    nationalities: undefined,
    identificationTypes: undefined,
    marketingConsents: undefined,
    referralCode: storedReferralCode || undefined,
    passwordSet: false,
    done: false,
  };
}
export const useRegisterStore = create<RegisterStateWithActions>()(
  devtools((set) => ({
    ...initialState(),
    setAccountType: (args) => set({ accountType: args }),
    setAccountLimit: (args) => set({ accountLimit: args }),
    setPassword: (args) => set({ password: args, passwordSet: true }),
    updateEmailStep: (args) =>
      set({ email: args.email, registrationId: args.id }),
    setMobile: (args) => set({ mobile: args, otpConfirmed: false }),
    setCountry: (args) => set({ country: args.country }),
    setAddressInfo: (args) => set({ addressInfo: args.addressInfo }),
    setOtpConfirmed: () => set({ otpConfirmed: true, done: true }),
    reset: () => set({ ...initialState() }),
    setPersonalInfo: (args) => set({ personalInfo: args.personalInfo }),
    setPasswordSet: () => set({ passwordSet: true, done: true }),
    setMarketingConsents: (args) => set({ marketingConsents: args }),
    setReferralCode: (code) => {
      // Update sessionStorage when state changes
      if (code) {
        setReferralCodeInStorage(code);
      }
      set({ referralCode: code });
    },
    setRefData: (args) =>
      set({
        nationalities: args.nationalities,
        sourceOfFunds: args.sourceOfFunds,
        occupations: args.occupations,
        identificationTypes: args.identificationTypes,
      }),
  })),
);
